/**
 * Agregar lugar — poner un pin en el mapa.
 *
 * No requiere Visit Token (`docs/09` §0.1): un `Place` es un dato factual, y
 * esta pantalla no pide prueba de nada ni la insinúa.
 *
 * Desde ADR-020 publicar una `Recommendation` tampoco exige token — lleva
 * sello o no lo lleva. Lo que sigue costando caminar es el sello.
 *
 * Dos reglas de `docs/03` gobiernan el flujo:
 *
 * 1. **§5.1 — pre-prompt obligatorio.** El diálogo nativo de ubicación no
 *    se dispara sin una pantalla propia que diga el beneficio concreto. Un
 *    permiso denegado es casi irrecuperable: gastarlo en frío es un error
 *    que no se deshace.
 * 2. **§4.1 — nunca un callejón.** Sin ubicación no hay punto y sin punto
 *    no hay pin. La salida real es **arrastrar el pin en el mapa**, y se
 *    ofrece cuando el GPS falla (`PinPicker`). El camino feliz —GPS que
 *    responde— no se toca: está probado de punta a punta.
 */

import { useState } from 'react';
import {
  PLACE_KIND_LABEL,
  PLACE_NAME_MIN,
  PlaceKind,
  type CreatePlaceInput,
} from '@gu/contracts';

import { ApiError, createPlace } from '../../shared/lib/api.js';
import { readPosition, type Position } from '../../shared/lib/geolocation.js';
import { PinPicker } from './PinPicker.js';
import { Boton } from '../../shared/ui/primitives.js';

export interface AddPlaceProps {
  onDone: (place: { id: string; name: string }) => void;
  onCancel: () => void;
  /** Para llevar a alguien al lugar que ya existía. */
  onSeePlace?: ((placeId: string) => void) | undefined;
}

/**
 * Por encima de esto, el pin cae en la otra cuadra.
 *
 * No bloquea: `docs/09` §4.2 acepta que un puesto de calle nazca con ±80 m
 * y se auto-corrija tras 5 tokens verdes. Solo se avisa, porque el pin es
 * de todos y quien lo pone puede moverse dos pasos.
 */
const ACCURACY_AVISO_M = 100;

export function AddPlace({ onDone, onCancel, onSeePlace }: AddPlaceProps) {
  const [posicion, setPosicion] = useState<Position | null>(null);
  const [falloGeo, setFalloGeo] = useState<'permission_denied' | 'otro' | null>(null);
  const [buscando, setBuscando] = useState(false);
  /** La salida sin GPS: poner el pin a mano en el mapa (deuda #5). */
  const [aMano, setAMano] = useState(false);

  const [nombre, setNombre] = useState('');
  const [kind, setKind] = useState<PlaceKind>('standard');
  const [error, setError] = useState<string | null>(null);
  const [duplicado, setDuplicado] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function pedirUbicacion() {
    setBuscando(true);
    setFalloGeo(null);
    const r = await readPosition();
    setBuscando(false);

    if (r.ok) {
      setPosicion(r.value);
      return;
    }
    // El permiso negado y el sensor mudo piden copys distintos: al primero
    // hay que explicarle el porqué, al segundo basta con reintentar.
    setFalloGeo(r.error === 'permission_denied' ? 'permission_denied' : 'otro');
  }

  async function guardar() {
    if (nombre.trim().length < PLACE_NAME_MIN || !posicion) return;

    setGuardando(true);
    setError(null);
    setDuplicado(null);
    try {
      const input: CreatePlaceInput = {
        name: nombre.trim(),
        lat: posicion.lat,
        lng: posicion.lng,
        kind,
        foodTypes: [],
      };
      onDone(await createPlace(input));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'place_duplicate') {
        // El id viaja en el cuerpo del error: es lo que convierte "ya
        // existe" en "ve a recomendarlo", que es lo que quería hacer.
        const id = e.body['existingId'];
        setDuplicado(typeof id === 'string' ? id : '');
        setError('Ese lugar ya está en el mapa.');
      } else {
        // El texto NO se borra: perder el trabajo de alguien es lo peor
        // que puede pasar en esta pantalla (`docs/09` §0.2).
        setError('No se pudo guardar. Revisa tu señal e inténtalo otra vez.');
      }
    } finally {
      setGuardando(false);
    }
  }

  const listo = posicion !== null;

  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--gu-surface-app)',
        padding: 'var(--gu-space-6) var(--gu-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gu-space-4)',
        maxWidth: '32rem',
        marginInline: 'auto',
      }}
    >
      {aMano ? (
        <>
          <h1 style={TITULO}>¿Dónde está?</h1>
          <PinPicker
            onConfirmar={(c) => {
              /*
               * `accuracyM: 0` no significa precisión perfecta: significa
               * que no hay medición del sensor. El pin lo puso una persona
               * mirando el mapa, así que el aviso de "señal floja" —que
               * dispara por encima de 100 m— no aplica aquí.
               *
               * Esto NO relaja la integridad: el Visit Token exige GPS
               * real y no lee este valor. Un pin a mano no publica nada
               * (ADR-013).
               */
              setPosicion({ lat: c.lat, lng: c.lng, accuracyM: 0 });
              setAMano(false);
            }}
            onCancelar={() => setAMano(false)}
          />
        </>
      ) : !listo ? (
        /*
         * EL PRE-PROMPT (`docs/03` §5.3). Pantalla propia, beneficio en una
         * frase, y una salida sin penalización. El diálogo nativo solo
         * aparece tras el tap del botón primario.
         */
        <>
          <h1 style={TITULO}>¿Dónde estás?</h1>
          <p style={SECUNDARIO}>
            Para saber que estás aquí y poner el pin en el punto exacto.{' '}
            <strong>No guardamos tu recorrido</strong> — solo la ubicación de
            este momento, mientras la app está abierta.
          </p>

          {falloGeo === 'permission_denied' && (
            <p role="alert" style={SECUNDARIO}>
              Necesitamos tu ubicación para poner el pin donde estás. Puedes
              activarla en los ajustes del navegador — o ponerlo a mano en el
              mapa.
            </p>
          )}
          {falloGeo === 'otro' && (
            <p role="alert" style={SECUNDARIO}>
              No pudimos ubicarte. A veces pasa bajo techo: sal un momento a
              donde se vea el cielo, o ponlo a mano en el mapa.
            </p>
          )}

          <Boton onClick={() => void pedirUbicacion()} disabled={buscando}>
            {buscando ? 'Buscando…' : falloGeo ? 'Volver a intentar' : 'Estoy aquí'}
          </Boton>

          {/*
           * La salida de la deuda #5. Aparece SOLO tras un fallo del GPS:
           * el camino feliz está probado de punta a punta y no se toca
           * (spec §2, decisión 5).
           *
           * `Boton variante="secundario"` y no un `<button style={ENLACE}>`:
           * es exactamente el mismo resultado visual —texto en
           * `--gu-text-link`, sin bloque de color que compita con la acción
           * primaria (`docs/06` §1.2)— pero con el área táctil que ya
           * garantiza la primitiva. Los `ENLACE` que quedan en este archivo
           * son anteriores; migrarlos es otra tarea.
           */}
          {falloGeo && (
            <Boton variante="secundario" onClick={() => setAMano(true)}>
              Ponerlo a mano en el mapa
            </Boton>
          )}

          <button type="button" onClick={onCancel} style={ENLACE}>
            Ahora no
          </button>
        </>
      ) : (
        <>
          <h1 style={TITULO}>¿Qué lugar es?</h1>

          {posicion && posicion.accuracyM > ACCURACY_AVISO_M && (
            <p style={SECUNDARIO}>
              La señal está floja aquí ({Math.round(posicion.accuracyM)} m de
              margen). El pin puede quedar en la otra cuadra; si puedes, sal un
              momento a donde se vea el cielo.
            </p>
          )}

          <label htmlFor="nombre-lugar" style={ETIQUETA}>
            ¿Cómo se llama? (nombre)
          </label>
          <input
            id="nombre-lugar"
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            style={CAMPO}
          />

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={ETIQUETA}>¿Qué tipo de lugar es?</legend>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--gu-space-2)',
                marginTop: 'var(--gu-space-2)',
              }}
            >
              {PlaceKind.options.map((k) => (
                <label
                  key={k}
                  style={{
                    display: 'flex',
                    gap: 'var(--gu-space-2)',
                    alignItems: 'center',
                    minHeight: 'var(--gu-touch-min)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="kind"
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  <span>{PLACE_KIND_LABEL[k]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {error && (
            <p role="alert" style={{ margin: 0, color: 'var(--gu-text-primary)' }}>
              {error}
            </p>
          )}

          {duplicado !== null && (
            <button
              type="button"
              onClick={() =>
                duplicado && onSeePlace ? onSeePlace(duplicado) : onCancel()
              }
              style={ENLACE}
            >
              Ver ese lugar
            </button>
          )}

          <Boton
            onClick={() => void guardar()}
            disabled={guardando || nombre.trim().length < PLACE_NAME_MIN || !posicion}
          >
            {guardando ? 'Guardando…' : 'Agregar al mapa'}
          </Boton>

          <button type="button" onClick={onCancel} style={ENLACE}>
            Cancelar
          </button>
        </>
      )}
    </main>
  );
}

const TITULO = {
  fontSize: 'var(--gu-text-h1)',
  lineHeight: 'var(--gu-leading-tight)',
  margin: 0,
} as const;

const SECUNDARIO = { margin: 0, color: 'var(--gu-text-secondary)' } as const;

const ETIQUETA = {
  fontSize: 'var(--gu-text-caption)',
  color: 'var(--gu-text-secondary)',
  fontWeight: 550,
} as const;

const CAMPO = {
  minHeight: 'var(--gu-touch-comfortable)',
  padding: '0 var(--gu-space-3)',
  borderRadius: 'var(--gu-radius-md)',
  border: '1px solid var(--gu-border-strong)',
  background: 'var(--gu-surface-raised)',
  // 16px o más: por debajo, iOS hace zoom al enfocar.
  fontSize: '1rem',
  width: '100%',
  boxSizing: 'border-box',
} as const;

const ENLACE = {
  background: 'none',
  border: 'none',
  color: 'var(--gu-text-link)',
  fontSize: 'var(--gu-text-body)',
  padding: 'var(--gu-space-3)',
  cursor: 'pointer',
  minHeight: 'var(--gu-touch-min)',
} as const;
